/**
 * Helper functions for flatting product loop
*/

/**
 * Creates a new location section with a unique ID
 * @param {Array} products - Current location sections
 * @returns {Object} - New location section object
 */
export const flattenProductData = (products, orderField = null, orderType = 'ASC') => {
  // 1) Expand each product into 0…N rows, then flatten
  const flat = products.flatMap(product => {
    const stocks = product.supplier_products_stocks || [];

    // if no stocks, return one “empty” row
    if (stocks.length === 0) {
      return {
        id:                     product.id,
        supplier_id:            product.supplier_id,
        supplier:               product.supplier.first_name,
        supplier_product_number:product.supplier_product_number,
        supplier_product_name:  product.supplier_product_name,
        product_status:         product.product_status,
        warehouse_id:           null,
        warehouse_name:         null,
        is_ds_warehouse:        0,
        location_id:            null,
        location_name:          null,
        available_quantity:     null,
      };
    }

    // otherwise return one row per stock
    return stocks.map(stock => ({
      id:                     product.id,
      supplier_id:            product.supplier_id,
      supplier:               product.supplier.first_name,
      supplier_product_number:product.supplier_product_number,
      supplier_product_name:  product.supplier_product_name,
      product_status:         product.product_status,
      warehouse_id:           stock.warehouse_location?.warehouse_id ?? null,
      warehouse_name:         stock.warehouse_location?.warehouse?.name ?? null,
      is_ds_warehouse:        stock.warehouse_location?.warehouse?.is_ds_warehouse ?? 0,
      location_id:            stock.warehouse_location?.id ?? null,
      location_name:          stock.warehouse_location?.name ?? null,
      stock_id:               stock.id ?? null,
      available_quantity:     stock.available_quantity ?? null,
    }));
  });

  // 2) If no ordering, just return
  if (!orderField) return flat;

  // 3) Otherwise sort in place
  const dir = orderType.toLowerCase() === 'asc' ? 1 : -1;
  return flat.sort((a, b) => {
    const va = a[orderField] ?? '';
    const vb = b[orderField] ?? '';

    // nulls last
    if (va === null && vb === null) return 0;
    if (va === null) return 1;
    if (vb === null) return -1;

    // numeric vs. string
    if (typeof va === 'number' || orderField === 'available_quantity') {
      return dir * (Number(va) - Number(vb));
    }
    return dir * String(va).localeCompare(String(vb));
  });
};

export function preprocessRestockRows(rows, admin = false) {
  return rows.map(row => {
    const out = { ...row };
    if (out.sales_product_code) out.sales_product_code = String(out.sales_product_code).trim();
    if (out.product_status) {
      const status = String(out.product_status).toLowerCase();
      out.product_status = status === "enabled" ? 1 : 0;
    }
    if (out.is_ds_warehouse) {
      const status = String(out.is_ds_warehouse).toLowerCase();
      out.is_ds_warehouse = status === "yes" ? 1 : 0;
    }
    return out;
  });
}

export function getActiveFilters(filters) {
  return Object.entries(filters)
    .filter(([, value]) => value !== '')
    .reduce((acc, [k, v]) => ({ ...acc, [k]: v }), {});
}

/**
 * DS Warehouse options for filter dropdown
 */
export const IsDSWarehouseOptions = [
  {
    value: null,
    key: 'Select'
  },
  {
    value: true,
    key: 'Yes'
  },
  {
    value: false,
    key: 'No'
  }
];

/**
 * Generate export columns and content based on admin role
 * @param {Array} flattenData - Flattened stock data
 * @param {boolean} admin - Whether user is admin
 * @returns {Object} - Object containing columns and content arrays
 */
export function generateExportData(flattenData, admin = false) {
  let columns = [];
  let content = [];
  
  if (admin) {
    columns = [
      { label: 'id', value: 'id' },
      { label: 'supplier', value: 'supplier' },
      { label: 'supplier_product_name', value: 'supplier_product_name' },
      { label: 'supplier_product_number', value: 'supplier_product_number' },
      { label: 'warehouse_name', value: 'warehouse_name' },
      { label: 'location_name', value: 'location_name' },
      { label: 'available_quantity', value: 'available_quantity' },
      { label: 'product_status', value: 'product_status' },
      // { label: 'is_ds_warehouse', value: 'is_ds_warehouse' },
    ];
    content = flattenData.map(stock => ({
      id: stock.id,
      supplier: stock.supplier,
      supplier_product_name: stock.supplier_product_name,
      supplier_product_number: stock.supplier_product_number,
      warehouse_name: stock.warehouse_name,
      location_name: stock.location_name,
      available_quantity: stock.available_quantity,
      product_status: stock.product_status ? 'Enabled' : 'Disabled',
      // is_ds_warehouse: stock.is_ds_warehouse ? 'Yes' : 'No',
    }));
  } else {
    columns = [
      { label: 'supplier_product_name', value: 'supplier_product_name' },
      { label: 'supplier_product_number', value: 'supplier_product_number' },
      { label: 'warehouse_name', value: 'warehouse_name' },
      { label: 'location_name', value: 'location_name' },
      { label: 'available_quantity', value: 'available_quantity' },
    ];
    content = flattenData.map(stock => ({
      supplier_product_name: stock.supplier_product_name,
      supplier_product_number: stock.supplier_product_number,
      warehouse_name: stock.warehouse_name,
      location_name: stock.location_name,
      available_quantity: stock.available_quantity,
    }));
  }
  
  return { columns, content };
}
